import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { KeycloakService } from 'keycloak-angular';
import { Subject, switchMap, takeUntil, catchError, of } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalService } from '../../shared-services/global.service';
import { buildPortfolio, buildStrategicMap, extractProjects, mapPortfolioHeatmap } from './portfolio-heatmap.model';

@Component({
  selector: 'lib-portfolio-heatmap',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, MatDialogModule],
  templateUrl: './portfolio-heatmap.component.html',
  styleUrl: './portfolio-heatmap.component.scss',
})
export class PortfolioHeatmapComponent implements OnInit, OnDestroy {
  @Input() data: any;
  rows: any[] = [];
  engineFamilies: any[] = [];
  hasData = false;
  loading = true;
  error = false;
  projectCount = 0;
  strategic: any = { rows: [], projectCount: 0, unclassified: 0 };
  expandedCells = new Set<string>();
  @ViewChild('projectDetails', { static: true }) projectDetails!: TemplateRef<unknown>;
  private dialog = inject(MatDialog);
  private detailsDialog?: MatDialogRef<unknown>;
  private destroy$ = new Subject<void>();
  get showHeatmap(): boolean { return this.data?.view !== 'strategic'; }

  constructor(private config: ConfigService, private global: GlobalService, private keycloak: KeycloakService) {}

  ngOnInit(): void {
    this.global.filters$.pipe(
      switchMap(payload => {
        this.loading = true;
        this.error = false;
        this.closeDetails();
        this.expandedCells.clear();
        const fixed = Object.assign({}, ...this.data.additionalFilters);
        const filters = { source: this.data.sources, ...payload?.filter, ...fixed };
        const email = this.keycloak.getKeycloakInstance().idTokenParsed?.['email'] || '';
        return this.config.fetchData(this.data.apiUrl, this.data.functionName, filters, this.data, 'post', undefined, email)
          .pipe(catchError(() => { this.error = true; return of(null); }));
      }),
      takeUntil(this.destroy$)
    ).subscribe(response => {
      let projects: any[];
      if (this.data.responseMode === 'matrix') {
        const matrix = mapPortfolioHeatmap(this.data.heatmap, response, this.data.responseMapping);
        this.rows = matrix.rows;
        this.engineFamilies = matrix.engineFamilies;
        this.hasData = matrix.hasData;
        const unique = new Map<string, any>();
        this.rows.forEach(row => row.cells.forEach((cell: any) => cell.projects.forEach((project: any) => {
          const key = project.uuid || (project.vpcr_number ? `${project.source || ''}:${project.vpcr_number}` : project);
          unique.set(key, project);
        })));
        projects = [...unique.values()];
      } else {
        projects = extractProjects(response, this.data.respKey);
        this.rows = this.showHeatmap ? buildPortfolio(this.data.heatmap, projects) : [];
        this.engineFamilies = this.data.heatmap?.engineFamilies || [];
        this.hasData = projects.length > 0;
      }
      this.strategic = buildStrategicMap(this.data.strategicMap, projects);
      this.projectCount = this.data.responseMode === 'matrix' ? projects.length : new Set(this.rows.flatMap(row => row.cells.flatMap((cell: any) => cell.projects))).size;
      this.loading = false;
    });
  }

  refresh(): void { this.global.setFilters(this.global.getCurrentFilters()); }
  toggleNeeds(id: string): void {
    if (this.expandedCells.has(id)) this.expandedCells.delete(id);
    else this.expandedCells.add(id);
  }
  inspectNeed(need: any, group: any, driver: any): void {
    this.openDetails({ market: group.label, family: driver.label, projects: need.projects });
  }
  openDetails(cell: any): void {
    this.closeDetails();
    this.detailsDialog = this.dialog.open(this.projectDetails, {
      data: cell,
      width: '96vw',
      maxWidth: '1800px',
      maxHeight: '90dvh',
      ariaLabel: `${cell.market} / ${cell.family}`,
      restoreFocus: true,
    });
  }
  closeDetails(): void { this.detailsDialog?.close(); this.detailsDialog = undefined; }
  ngOnDestroy(): void { this.closeDetails(); this.destroy$.next(); this.destroy$.complete(); }
}
